import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Save, Trash2 } from 'lucide-react';
import { api } from '../../services/api';

const PROPAGACION_OPTIONS = ['Semillas', 'Estacas', 'Retoños'];
const USOS_OPTIONS = [
  'Frutal',
  'Ornamental',
  'Proteccion de cuencas',
  'Leña - carbon',
  'Maderables',
  'Cercas vivas',
  'Medicinal',
  'Sombio',
  'Postes',
];

function emptyForm() {
  return {
    especie: '',
    familia: '',
    genero: '',
    propagacion: [],
    usos: [],
  };
}

function normalizeArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
}

function MultiSelectField({ label, hint, options, values, onToggle }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
      <div className="text-sm font-medium text-gray-700">{label}</div>
      {hint ? <div className="mt-1 text-xs text-gray-500">{hint}</div> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => {
          const active = values.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                active
                  ? 'shadow-sm'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-emerald-400 hover:text-emerald-700'
              }`}
              style={
                active
                  ? {
                      backgroundColor: '#047857',
                      borderColor: '#047857',
                      color: '#ffffff',
                    }
                  : undefined
              }
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function EspeciesAmbientalModule({ location }) {
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.sgi.species.list({ location, limit: 1000, order: 'desc' });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setRows([]);
      setFeedback('No se pudo cargar la informacion de especies.');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleMulti = (field, option) => {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const exists = current.includes(option);
      return {
        ...prev,
        [field]: exists ? current.filter((item) => item !== option) : [...current, option],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      id: editingId || undefined,
      location,
      especie: String(form.especie || '').trim(),
      familia: String(form.familia || '').trim(),
      genero: String(form.genero || '').trim(),
      propagacion: normalizeArray(form.propagacion),
      usos: normalizeArray(form.usos),
    };
    if (!payload.especie || !payload.familia || !payload.genero) {
      setFeedback('Completa Especie, Familia y Genero.');
      return;
    }

    try {
      setSaving(true);
      setFeedback('');
      await api.sgi.species.upsert(payload);
      setForm(emptyForm());
      setEditingId('');
      setFeedback(editingId ? 'Registro actualizado correctamente.' : 'Registro guardado correctamente.');
      await loadRows();
    } catch (err) {
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setForm({
      especie: row.especie || '',
      familia: row.familia || '',
      genero: row.genero || '',
      propagacion: normalizeArray(row.propagacion),
      usos: normalizeArray(row.usos),
    });
    setEditingId(row.id);
    setFeedback(`Editando registro de ${row.especie || 'especie'}.`);
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(`¿Eliminar el registro ${row.especie || 'seleccionado'}?`);
    if (!confirmed) return;
    try {
      await api.sgi.species.delete({ id: row.id });
      if (editingId === row.id) {
        setEditingId('');
        setForm(emptyForm());
      }
      setFeedback('Registro eliminado.');
      await loadRows();
    } catch (err) {
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const handleCancel = () => {
    setEditingId('');
    setForm(emptyForm());
    setFeedback('');
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-lime-50 p-4 shadow-sm">
        <div className="text-base font-semibold text-gray-800">Especies</div>
        <div className="mt-1 text-sm text-gray-600">
          Registra la informacion de especie, familia, genero, propagacion y usos para la sede seleccionada.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Especie</span>
            <input
              type="text"
              value={form.especie}
              onChange={handleChange('especie')}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              placeholder="Ingresa la especie"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Familia</span>
            <input
              type="text"
              value={form.familia}
              onChange={handleChange('familia')}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              placeholder="Ingresa la familia"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Genero</span>
            <input
              type="text"
              value={form.genero}
              onChange={handleChange('genero')}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              placeholder="Ingresa el genero"
            />
          </label>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <MultiSelectField
            label="Propagacion"
            hint="Puedes escoger mas de una opcion"
            options={PROPAGACION_OPTIONS}
            values={normalizeArray(form.propagacion)}
            onToggle={(option) => handleToggleMulti('propagacion', option)}
          />
          <MultiSelectField
            label="Usos"
            hint="Puedes escoger mas de una opcion"
            options={USOS_OPTIONS}
            values={normalizeArray(form.usos)}
            onToggle={(option) => handleToggleMulti('usos', option)}
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700"
          >
            <Save size={16} />
            {saving ? 'Guardando...' : (editingId ? 'Actualizar' : 'Guardar')}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancelar
            </button>
          )}
        </div>

        {feedback && (
          <div className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {feedback}
          </div>
        )}
      </form>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 text-sm font-semibold text-gray-800">Registros guardados</div>
        {loading ? (
          <div className="text-sm text-gray-500">Cargando registros...</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-500">Aun no hay registros de especies.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="grid gap-2 text-sm text-gray-700 md:grid-cols-2 xl:grid-cols-5 md:gap-6">
                  <div><span className="font-semibold">Especie:</span> {row.especie || '-'}</div>
                  <div><span className="font-semibold">Familia:</span> {row.familia || '-'}</div>
                  <div><span className="font-semibold">Genero:</span> {row.genero || '-'}</div>
                  <div><span className="font-semibold">Propagacion:</span> {normalizeArray(row.propagacion).join(', ') || '-'}</div>
                  <div><span className="font-semibold">Usos:</span> {normalizeArray(row.usos).join(', ') || '-'}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(row)}
                    className="inline-flex items-center gap-1 rounded-xl border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-xs font-medium text-yellow-700 transition hover:bg-yellow-100"
                  >
                    <Pencil size={14} />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(row)}
                    className="inline-flex items-center gap-1 rounded-xl border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-100"
                  >
                    <Trash2 size={14} />
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
